import React, { createContext, useContext, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types'; 
import { useNavigate, useLocation } from 'react-router-dom';
import { COMPLETE_HELP_ROUTES } from '../routes/routes.config';

// Estado inicial del contexto
const initialState = {
  // Estructura de navegación estática (rutas principales)
  staticRoutes: {
    main: [
      {
        key: 'main',
        route: COMPLETE_HELP_ROUTES.main,
        icon: 'home'
      },
      {
        key: 'tickets',
        route: COMPLETE_HELP_ROUTES.tickets,
        icon: 'ticket'
      }
    ],
    documentation: {
      section: 'documentation',
      title: 'Documentación',
      icon: 'documentation',
      isExpandable: true,
      items: [
        {
          key: 'tracking',
          route: COMPLETE_HELP_ROUTES.tracking,
          icon: 'tracking'
        }
      ]
    },
    privacy: {
      section: 'privacy',
      title: 'Privacidad y datos',
      icon: 'privacy',
      isExpandable: true,
      items: [
        {
          key: 'informationProtection',
          route: COMPLETE_HELP_ROUTES.informationProtection
        }
      ]
    },
    contact: []
  },
  
  // Estructura de navegación jerárquica (explorador de archivos)
  fileTree: {
    id: 'root',
    name: 'Documentación',
    type: 'folder',
    isExpanded: true,
    children: [
      {
        id: 'backend',
        name: 'Backend',
        type: 'folder',
        isExpanded: false,
        path: '/help/documentation/backend',
        children: [
          {
            id: 'index-js',
            name: 'index.js',
            type: 'file',
            path: '/help/documentation/backend/index-js',
            description: 'Archivo principal de ejecución del servidor',
            content: 'backend/src/index.js'
          },
          {
            id: 'whatsapp-manager',
            name: 'whatsappManager.js',
            type: 'file',
            path: '/help/documentation/backend/whatsapp-manager',
            description: 'Gestor de conexiones WhatsApp y WebSocket',
            content: 'backend/src/whatsappManager.js'
          },
          {
            id: 'app-js',
            name: 'app.js',
            type: 'file',
            path: '/help/documentation/backend/app-js',
            description: 'Configuración de Express y middleware',
            content: 'backend/src/app.js'
          },
          {
            id: 'session-manager',
            name: 'sessionManager.js',
            type: 'file',
            path: '/help/documentation/backend/session-manager',
            description: 'Gestor de sesiones en memoria',
            content: 'backend/src/sessionManager.js'
          }
        ]
      },
      {
        id: 'frontend',
        name: 'Frontend',
        type: 'folder',
        isExpanded: false,
        path: '/help/documentation/frontend',
        children: [
          {
            id: 'help-system',
            name: 'Sistema de Ayuda',
            type: 'folder',
            isExpanded: false,
            children: [
              {
                id: 'help-context',
                name: 'HelpContext.js',
                type: 'file',
                path: '/help/documentation/frontend/help-context',
                description: 'Contexto para navegación y búsqueda',
                content: 'frontend/src/context/HelpContext.js'
              },
              {
                id: 'help-component',
                name: 'Help.jsx',
                type: 'file',
                path: '/help/documentation/frontend/help-component',
                description: 'Componente principal del sistema de ayuda',
                content: 'frontend/src/page/help/Help.jsx'
              },
              {
                id: 'help-leftnav',
                name: 'HelpLeftNav.jsx',
                type: 'file',
                path: '/help/documentation/frontend/help-leftnav',
                description: 'Navegación lateral del sistema de ayuda',
                content: 'frontend/src/components/layout/HelpLeftNav.jsx'
              }
            ]
          }
        ]
      },
      {
        id: 'api',
        name: 'API',
        type: 'folder',
        isExpanded: false,
        path: '/help/documentation/api',
        children: [
          {
            id: 'auth-endpoints',
            name: 'Autenticación',
            type: 'file',
            path: '/help/documentation/api/auth',
            description: 'Endpoints de autenticación y autorización',
            content: 'api/auth'
          },
          {
            id: 'vehicle-endpoints',
            name: 'Vehículos',
            type: 'file',
            path: '/help/documentation/api/vehicles',
            description: 'Endpoints para gestión de vehículos',
            content: 'api/vehicles'
          },
          {
            id: 'user-endpoints',
            name: 'Usuarios',
            type: 'file',
            path: '/help/documentation/api/users',
            description: 'Endpoints para gestión de usuarios',
            content: 'api/users'
          }
        ]
      },
      {
        id: 'database',
        name: 'Base de Datos',
        type: 'folder',
        isExpanded: false,
        path: '/help/documentation/database',
        children: [
          {
            id: 'schema',
            name: 'Esquema',
            type: 'file',
            path: '/help/documentation/database/schema',
            description: 'Esquema de la base de datos',
            content: 'database/schema'
          },
          {
            id: 'migrations',
            name: 'Migraciones',
            type: 'file',
            path: '/help/documentation/database/migrations',
            description: 'Sistema de migraciones',
            content: 'database/migrations'
          }
        ]
      }
    ]
  },
  
  // Estado de navegación
  navigation: {
    currentPath: '/help',
    breadcrumbs: ['Centro de Ayuda'],
    searchQuery: '',
    searchResults: [],
    isSearching: false
  },
  
  // Estado de UI
  ui: {
    leftNavExpanded: true,
    documentationMode: false, // false = rutas estáticas, true = explorador de archivos
    searchExpanded: false,
    currentFile: null,
    fileContent: null,
    expandedSections: {
      documentation: true,
      privacy: false
    }
  }
};

// Tipos de acciones
const ACTION_TYPES = {
  SET_CURRENT_PATH: 'SET_CURRENT_PATH',
  TOGGLE_LEFT_NAV: 'TOGGLE_LEFT_NAV',
  TOGGLE_DOCUMENTATION_MODE: 'TOGGLE_DOCUMENTATION_MODE',
  TOGGLE_SECTION: 'TOGGLE_SECTION',
  TOGGLE_FOLDER: 'TOGGLE_FOLDER',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  SET_IS_SEARCHING: 'SET_IS_SEARCHING',
  TOGGLE_SEARCH: 'TOGGLE_SEARCH',
  SET_CURRENT_FILE: 'SET_CURRENT_FILE',
  SET_FILE_CONTENT: 'SET_FILE_CONTENT',
  UPDATE_BREADCRUMBS: 'UPDATE_BREADCRUMBS'
};

// Reducer
const helpReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_CURRENT_PATH:
      return {
        ...state,
        navigation: {
          ...state.navigation,
          currentPath: action.payload
        }
      };

    case ACTION_TYPES.TOGGLE_LEFT_NAV:
      return {
        ...state,
        ui: {
          ...state.ui,
          leftNavExpanded: !state.ui.leftNavExpanded
        }
      };

    case ACTION_TYPES.TOGGLE_DOCUMENTATION_MODE:
      return {
        ...state,
        ui: {
          ...state.ui,
          documentationMode: !state.ui.documentationMode
        }
      };

    case ACTION_TYPES.TOGGLE_SECTION:
      return {
        ...state,
        ui: {
          ...state.ui,
          expandedSections: {
            ...state.ui.expandedSections,
            [action.payload]: !state.ui.expandedSections[action.payload]
          }
        }
      };

    case ACTION_TYPES.TOGGLE_FOLDER:
      return {
        ...state,
        fileTree: toggleFolderInTree(state.fileTree, action.payload)
      };

    case ACTION_TYPES.SET_SEARCH_QUERY:
      return {
        ...state,
        navigation: {
          ...state.navigation,
          searchQuery: action.payload
        }
      };

    case ACTION_TYPES.SET_SEARCH_RESULTS:
      return {
        ...state,
        navigation: {
          ...state.navigation,
          searchResults: action.payload
        }
      };

    case ACTION_TYPES.SET_IS_SEARCHING:
      return {
        ...state,
        navigation: {
          ...state.navigation,
          isSearching: action.payload
        }
      };

    case ACTION_TYPES.TOGGLE_SEARCH:
      return {
        ...state,
        ui: {
          ...state.ui,
          searchExpanded: !state.ui.searchExpanded
        }
      };

    case ACTION_TYPES.SET_CURRENT_FILE:
      return {
        ...state,
        ui: {
          ...state.ui,
          currentFile: action.payload
        }
      };

    case ACTION_TYPES.SET_FILE_CONTENT:
      return {
        ...state,
        ui: {
          ...state.ui,
          fileContent: action.payload
        }
      };

    case ACTION_TYPES.UPDATE_BREADCRUMBS:
      return {
        ...state,
        navigation: {
          ...state.navigation,
          breadcrumbs: action.payload
        }
      };

    default:
      return state;
  }
};

// Función auxiliar para toggle de carpetas
const toggleFolderInTree = (tree, folderId) => {
  if (tree.id === folderId) {
    return {
      ...tree,
      isExpanded: !tree.isExpanded
    };
  }

  if (tree.children) {
    return {
      ...tree,
      children: tree.children.map(child => toggleFolderInTree(child, folderId))
    };
  }

  return tree;
};

// Función para buscar en el árbol de archivos
const searchInTree = (tree, query) => {
  const results = [];

  const searchRecursive = (node, path = '') => {
    const currentPath = path ? `${path}/${node.name}` : node.name;

    if (node.name.toLowerCase().includes(query.toLowerCase()) ||
        (node.description && node.description.toLowerCase().includes(query.toLowerCase()))) {
      results.push({
        ...node,
        fullPath: currentPath
      });
    }

    if (node.children) {
      node.children.forEach(child => searchRecursive(child, currentPath));
    }
  };

  searchRecursive(tree);
  return results;
};

// Función para obtener breadcrumbs
const getBreadcrumbs = (tree, currentPath) => {
  const breadcrumbs = ['Centro de Ayuda'];

  const findPath = (node, path = '') => {
    if (node.path === currentPath) {
      breadcrumbs.push(node.name);
      return true;
    }

    if (node.children) {
      for (const child of node.children) {
        if (findPath(child, path)) {
          breadcrumbs.push(node.name);
          return true;
        }
      }
    }

    return false;
  };

  findPath(tree);
  return breadcrumbs.reverse();
};

// Contexto
const HelpContext = createContext();

// Provider
export const HelpProvider = ({ children }) => {
  const [state, dispatch] = useReducer(helpReducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();

  // Acciones
  const setCurrentPath = useCallback((path) => {
    dispatch({ type: ACTION_TYPES.SET_CURRENT_PATH, payload: path });
  }, []);

  const toggleLeftNav = useCallback(() => {
    dispatch({ type: ACTION_TYPES.TOGGLE_LEFT_NAV });
  }, []);

  const toggleDocumentationMode = useCallback(() => {
    dispatch({ type: ACTION_TYPES.TOGGLE_DOCUMENTATION_MODE });
  }, []);

  const toggleSection = useCallback((section) => {
    dispatch({ type: ACTION_TYPES.TOGGLE_SECTION, payload: section });
  }, []);

  const toggleFolder = useCallback((folderId) => {
    dispatch({ type: ACTION_TYPES.TOGGLE_FOLDER, payload: folderId });
  }, []);

  const setSearchQuery = useCallback((query) => {
    dispatch({ type: ACTION_TYPES.SET_SEARCH_QUERY, payload: query });
    
    if (query.trim()) {
      dispatch({ type: ACTION_TYPES.SET_IS_SEARCHING, payload: true });
      const results = searchInTree(state.fileTree, query);
      dispatch({ type: ACTION_TYPES.SET_SEARCH_RESULTS, payload: results });
      dispatch({ type: ACTION_TYPES.SET_IS_SEARCHING, payload: false });
    } else {
      dispatch({ type: ACTION_TYPES.SET_SEARCH_RESULTS, payload: [] });
    }
  }, [state.fileTree]);

  const toggleSearch = useCallback(() => {
    dispatch({ type: ACTION_TYPES.TOGGLE_SEARCH });
  }, []);

  const setCurrentFile = useCallback((file) => {
    dispatch({ type: ACTION_TYPES.SET_CURRENT_FILE, payload: file });
  }, []);

  const setFileContent = useCallback((content) => {
    dispatch({ type: ACTION_TYPES.SET_FILE_CONTENT, payload: content });
  }, []);

  const navigateToFile = useCallback((file) => {
    if (file.path) {
      navigate(file.path);
      setCurrentFile(file);
      setCurrentPath(file.path);
      
      // Actualizar breadcrumbs
      const breadcrumbs = getBreadcrumbs(state.fileTree, file.path);
      dispatch({ type: ACTION_TYPES.UPDATE_BREADCRUMBS, payload: breadcrumbs });
    }
  }, [navigate, setCurrentFile, setCurrentPath, state.fileTree]);

  // Efecto para sincronizar con la ubicación actual
  React.useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname, setCurrentPath]);

  const value = {
    // Estado
    staticRoutes: state.staticRoutes,
    fileTree: state.fileTree,
    navigation: state.navigation,
    ui: state.ui,
    
    // Acciones
    setCurrentPath,
    toggleLeftNav,
    toggleDocumentationMode,
    toggleSection,
    toggleFolder,
    setSearchQuery,
    toggleSearch,
    setCurrentFile,
    setFileContent,
    navigateToFile,
    
    // Utilidades
    searchInTree: (query) => searchInTree(state.fileTree, query),
    getBreadcrumbs: (path) => getBreadcrumbs(state.fileTree, path)
  };

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  );
};

// Hook personalizado
export const useHelp = () => {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error('useHelp debe ser usado dentro de un HelpProvider');
  }
  return context;
};
HelpProvider.propTypes = {
  children: PropTypes.node.isRequired,
};